import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import MachinesList from './MachinesList';
import ViewMachine from './ViewMachine';
import ResizablePanels from '../../components/Layout/ResizablePanels';
import './Machines.css';

function Machines({ initialViewMode, initialMachineId }) {
  const { t } = useTranslation('production');
  const [selectedMachineId, setSelectedMachineId] = useState(initialMachineId || null);
  const [viewMode, setViewMode] = useState(initialViewMode || 'list'); // 'list', 'view', 'edit', 'create'

  useEffect(() => {
    // Set toolbar based on current mode
    if (window.setToolbar) {
      if (viewMode === 'list') {
        window.setToolbar({
          title: t('machines.toolbar.titles.machines'),
          buttons: [
            {
              label: t('machines.toolbar.buttons.newMachine'),
              icon: '➕',
              onClick: handleNew,
              variant: 'primary'
            },
            {
              label: t('machines.toolbar.buttons.refresh'),
              icon: '🔄',
              onClick: handleRefresh
            }
          ]
        });
      } else if (viewMode === 'view') {
        window.setToolbar({
          title: t('machines.toolbar.titles.viewMachine'),
          showBack: true,
          onBack: handleBack,
          buttons: [
            {
              label: t('machines.toolbar.buttons.edit'),
              icon: '✏️',
              onClick: handleEdit,
              variant: 'secondary'
            },
            {
              label: t('machines.toolbar.buttons.delete'),
              icon: '🗑️',
              onClick: handleDelete,
              variant: 'danger'
            }
          ]
        });
      } else if (viewMode === 'edit') {
        window.setToolbar({
          title: t('machines.toolbar.titles.editMachine'),
          showBack: false,
          buttons: [
            {
              label: t('machines.toolbar.buttons.cancel'),
              onClick: handleCancel,
              variant: 'secondary'
            },
            {
              label: t('machines.toolbar.buttons.save'),
              icon: '💾',
              onClick: handleSave,
              variant: 'primary'
            }
          ]
        });
      } else if (viewMode === 'create') {
        window.setToolbar({
          title: t('machines.toolbar.titles.newMachine'),
          showBack: false,
          buttons: [
            {
              label: t('machines.toolbar.buttons.cancel'),
              onClick: handleCancel,
              variant: 'secondary'
            },
            {
              label: t('machines.toolbar.buttons.create'),
              icon: '➕',
              onClick: handleSave,
              variant: 'primary'
            }
          ]
        });
      }
    }

    return () => {
      if (window.setToolbar) {
        window.setToolbar(null);
      }
    };
  }, [viewMode]);

  const handleSelectMachine = (machineId) => {
    setSelectedMachineId(machineId);
    setViewMode('view');
  };

  const handleNew = () => {
    setSelectedMachineId(null);
    setViewMode('create');
  };

  const handleEdit = () => {
    setViewMode('edit');
  };

  const handleDelete = () => {
    // Delete will be handled in ViewMachine component
    if (window.viewMachineRef && window.viewMachineRef.handleDelete) {
      window.viewMachineRef.handleDelete();
    }
  };

  const handleSave = () => {
    // Save will be handled in ViewMachine component
    if (window.viewMachineRef && window.viewMachineRef.handleSave) {
      window.viewMachineRef.handleSave();
    }
  };

  const handleCancel = () => {
    if (viewMode === 'create') {
      setViewMode('list');
      setSelectedMachineId(null);
    } else {
      setViewMode('view');
    }
  };

  const handleBack = () => {
    setViewMode('list');
    setSelectedMachineId(null);
  };

  const handleRefresh = () => {
    // Trigger refresh in MachinesList
    if (window.machinesListRef && window.machinesListRef.fetchMachines) {
      window.machinesListRef.fetchMachines();
    }
  };

  const handleMachineSaved = (machine) => {
    setSelectedMachineId(machine.machine_id);
    setViewMode('view');
    // Refresh the list
    if (window.machinesListRef && window.machinesListRef.fetchMachines) {
      window.machinesListRef.fetchMachines();
    }
  };

  const handleMachineCreated = (machineId) => {
    setSelectedMachineId(machineId);
    setViewMode('view');
    // Refresh the list
    if (window.machinesListRef && window.machinesListRef.fetchMachines) {
      window.machinesListRef.fetchMachines();
    }
  };

  return (
    <div className="machines-page">
      <ResizablePanels
        leftPanel={
          <MachinesList
            ref={ref => window.machinesListRef = ref}
            onSelectMachine={handleSelectMachine}
            selectedMachineId={selectedMachineId}
            onNew={handleNew}
          />
        }
        rightPanel={
          viewMode !== 'list' ? (
            <ViewMachine
              ref={ref => window.viewMachineRef = ref}
              machineId={selectedMachineId}
              onBack={handleBack}
              editMode={viewMode === 'edit'}
              createMode={viewMode === 'create'}
              onSave={handleMachineSaved}
              onCancel={handleCancel}
              onEdit={handleEdit}
              onMachineCreated={handleMachineCreated}
            />
          ) : null
        }
        leftCollapsed={viewMode !== 'list'}
        defaultLeftWidth={40}
        minLeftWidth={30}
        maxLeftWidth={70}
      />
    </div>
  );
}

export default Machines;